import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

const ALL_MARKETS = ['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA']
const MARKET_LABELS = {
  'SE':'Sweden','NL-BE':'Netherlands/Belgium','UK':'United Kingdom',
  'US':'United States','AU':'Australia','NO':'Norway',
  'GE':'Germany','FR':'France','DK':'Denmark','FI':'Finland','CA':'Canada'
}

export const DEFAULT_COLUMNS = {
  assetprep: { product_id:true, market:true, brand_name:true, logo_link:true, deadline:true, status:true, completed_by:true, date_completed:true },
  products: {
    product_id:true, product_name:true, sourcing_cost_eur:true, total_cogs:true,
    selling_price_eur:true, profit:true, profit_margin:true, aliexpress_link:true,
    competitor_product_url:true, competitor_advertorial_url:true, competitor_winning_ad_links:true,
    offer_to_use:true, work_status:true, approval:true, approved_by:true, review_notes:true,
    sourcing_cost_usd:false, shipping_cost_usd:false, shipping_cost_eur:false,
    shopify_fee:false, currency_conv:false, total_fees:false, be_roas:false,
    advertorial_link:false, competitor_page_ad_url:false,
    competitor_ad_copy_primary:false, competitor_ad_copy_headline:false,
    competitor_ad_creative_link:false, research_notes:false,
    platform_found_on:false, competitor_price:false, ad_running_duration:false, ad_countries:false, scale_signal:false, ad_hook:false, ad_angle:false, market_saturated:false, desire_targeted:false, customer_concerns:false, problem_addressed:false, target_audience:false, competitor_positioning:false,
  },
  funnel: {
    market:true, assigned_to:true, comp_adv_html:true, comp_sales_html:true, aliexpress_link:true, product_id:true, brand_name:true, competitor_product_url:true,
    competitor_advertorial_url:true, offer_to_use:true, funnel_link:true, advertorial_link:true,
    content_ready:true, funnel_name:true, deadline:true, funnel_status:true, completed:true,
    approval:true, approved_by:true, revision_notes:true,
    competitor_page_ad_url:false, audit_report:false,
  },
  creatives: { deadline:true,
    market:true, assigned_to:true, aliexpress_link:true, assets_status:false, product_id:true, brand_name:true, competitor_product_url:true,
    competitor_winning_ad_links:true, creative_file_link:true, creative_status:true,
    completed:true, approval:true, approved_by:true, revision_notes:true,
    competitor_ad_creative_link:false,
  },
  adcopy: { deadline:true,
    market:true, assigned_to:true, brand_name:true, funnel_link:true, ai_copy_primary:true,
    ai_copy_headline:true, adapted_copy_headline:true, adapted_copy_primary:true,
    status:true, approval:true,
    product_id:false, competitor_ad_copy_primary:false, competitor_ad_copy_headline:false,
    completed:false, approved_by:false, revision_notes:false,
  },
  scheduling: {
    readiness:true, product_id:true,
    market:true, brand_name:true, creative_file_link:true,
    ai_copy_primary:true, ai_copy_headline:true, adapted_copy_primary:true, adapted_copy_headline:true, funnel_link:true, advertorial_link:true,
    ad_account:true, page:true, campaign_name:true, daily_budget_eur:true,
    scheduled_date:true, campaign_status:true,
  },
  creativerequests: {
    product_id:true, brand_name:true, market:true, reference_ad_link:true,
    creative_type:true, quantity:true, brief:true, priority:true, deadline:true, status:true,
    assigned_editor:true, delivered_link:true, requested_by:true,
  },
  winning: {
    market:true, product_id:true, brand_name:true, be_roas:true, profit:true,
    creative_file_link:true, funnel_link:true, ad_account:true, campaign_name:true,
    date_added_as_winner:true, last_creative_added_date:true, comments:true,
  }
}

const COLUMN_LABELS = {
  product_id:'Product ID', product_name:'Product Name',
  sourcing_cost_usd:'Sourcing Cost (USD)', sourcing_cost_eur:'Sourcing Cost (€)',
  shipping_cost_usd:'Shipping Cost (USD)', shipping_cost_eur:'Shipping Cost (€)',
  total_cogs:'Total COGs (€)', profit_margin:'Profit Margin (%)', selling_price_eur:'Selling Price (€)',
  deadline:'Deadline', advertorial_link:'Advertorial Link',
  shopify_fee:'Shopify Fee', currency_conv:'Currency Conv (2%)',
  total_fees:'Total Fees (€)', profit:'Profit (€)', be_roas:'BE ROAS',
  competitor_product_url:'Competitor Product URL', competitor_advertorial_url:'Advertorial URL',
  competitor_page_ad_url:'Competitor Page Ad URL', competitor_winning_ad_links:'Winning Ad Links',
  competitor_ad_copy_primary:'Comp Ad Copy Primary', competitor_ad_copy_headline:'Comp Ad Copy Headline',
  competitor_ad_creative_link:'Comp Creative Link', offer_to_use:'Offer to Use', research_notes:'Research Notes',
  market:'Market', funnel_link:'Funnel Link', audit_report:'Audit Report',
  brand_name:'Brand Name', funnel_name:'Funnel Name', funnel_status:'Funnel Status', date_completed:'Date Completed',
  creative_file_link:'Creative File/Link', creative_status:'Creative Status',
  assigned_editor:'Assigned Editor', revision_notes:'Revision Notes',
  ai_copy_primary:'AI Copy — Primary', ai_copy_headline:'AI Copy — Headline',
  adapted_copy_primary:'Adapted Primary', adapted_copy_headline:'Adapted Headline',
  status:'Status', approved_by:'Approved By', ad_account:'Ad Account',
  campaign_name:'Campaign Name', daily_budget_eur:'Daily Budget (€)',
  scheduled_date:'Scheduled Date', campaign_status:'Campaign Status',
  date_added_as_winner:'Date Added as Winner', last_creative_added_date:'Last Creative Date',
  comments:'Comments', competitor_page_ad_url:'Comp Page Ad URL',
}

const SECTION_LABELS = {
  products:'Product Research', funnel:'Funnel Building',
  creatives:'Creatives', adcopy:'Ad Copy',
  scheduling:'Ad Scheduling', winning:'Winning Products',
}

export function useColumns(section) {
  const key = `cols_${section}`
  const [cols, setCols] = useState(() => {
    try {
      const saved = localStorage.getItem(key)
      return saved ? { ...DEFAULT_COLUMNS[section], ...JSON.parse(saved) } : DEFAULT_COLUMNS[section] || {}
    } catch { return DEFAULT_COLUMNS[section] || {} }
  })
  const isVisible = col => cols[col] !== false
  return { cols, isVisible }
}

const DEFAULT_FORMULAS = {
  usd_eur_rate: 0.93, shopify_pct: 4.99, shopify_fixed: 0.30, currency_conv_pct: 2.0,
}

function Toggle({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{
      width:38, height:21, borderRadius:11,
      background: checked ? 'var(--accent)' : 'var(--bg)',
      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border2)'}`,
      position:'relative', cursor:'pointer', transition:'all 0.2s', flexShrink:0
    }}>
      <div style={{
        position:'absolute', top:2, left: checked ? 18 : 2,
        width:15, height:15, borderRadius:'50%',
        background: checked ? '#fff' : 'var(--text3)',
        transition:'left 0.2s', boxShadow: checked ? '0 1px 4px rgba(0,0,0,0.3)' : 'none'
      }}/>
    </div>
  )
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('markets')
  const [activeMarkets, setActiveMarkets] = useState([])
  const [formulas, setFormulas] = useState(DEFAULT_FORMULAS)
  const [colSection, setColSection] = useState('products')
  const [colSettings, setColSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [customMarket, setCustomMarket] = useState('')
  const [editors, setEditors] = useState([])
  const [approvers, setApprovers] = useState([])
  const [adAccounts, setAdAccounts] = useState([])
  const [funnelBuilders, setFunnelBuilders] = useState([])
  const [newFunnelBuilder, setNewFunnelBuilder] = useState('')
  const [newEditor, setNewEditor] = useState('')
  const [newApprover, setNewApprover] = useState('')
  const [newAdAccount, setNewAdAccount] = useState('')
  const [currencyRates, setCurrencyRates] = useState({ SEK: 11.3, DKK: 7.46, NOK: 11.5 })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const s = await api.getSettings()
      setActiveMarkets(s.active_markets || ALL_MARKETS)
      setFormulas({ ...DEFAULT_FORMULAS, ...(s.formulas || {}) })
      setEditors(s.editors || [])
      setApprovers(s.approvers || [])
      setAdAccounts(s.ad_accounts || [])
      setFunnelBuilders(s.who_creates || [])
      setCurrencyRates({ SEK: 11.3, DKK: 7.46, NOK: 11.5, ...(s.currency_rates || {}) })
    } catch (err) { toast.error(err.message) }
    const cols = {}
    Object.keys(DEFAULT_COLUMNS).forEach(sec => {
      try {
        const saved = localStorage.getItem(`cols_${sec}`)
        cols[sec] = saved ? { ...DEFAULT_COLUMNS[sec], ...JSON.parse(saved) } : { ...DEFAULT_COLUMNS[sec] }
      } catch { cols[sec] = { ...DEFAULT_COLUMNS[sec] } }
    })
    setColSettings(cols)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function saveMarkets() {
    setSaving(true)
    try { await api.updateSetting('active_markets', activeMarkets); toast.success('Markets saved!') }
    catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function saveFormulas() {
    setSaving(true)
    try { await api.updateSetting('formulas', formulas); toast.success('Formulas saved!') }
    catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function saveCurrencyRates() {
    setSaving(true)
    try { await api.updateSetting('currency_rates', currencyRates); toast.success('Currency rates saved!') }
    catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  function toggleMarket(m) {
    setActiveMarkets(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])
  }

  async function saveList(key, list) {
    try { await api.updateSetting(key, list); toast.success('Saved!') }
    catch (err) { toast.error(err.message) }
  }

  function addCustomMarket() {
    const m = (customMarket || '').trim().toUpperCase()
    if (!m) { return }
    if (activeMarkets.includes(m)) { toast.error('Market already active'); return }
    setActiveMarkets(prev => [...prev, m])
    setCustomMarket('')
  }

  function toggleCol(section, col) {
    setColSettings(prev => {
      const updated = { ...prev, [section]: { ...prev[section], [col]: !prev[section][col] } }
      localStorage.setItem(`cols_${section}`, JSON.stringify(updated[section]))
      return updated
    })
  }

  function resetCols(section) {
    const reset = { ...DEFAULT_COLUMNS[section] }
    setColSettings(prev => ({ ...prev, [section]: reset }))
    localStorage.setItem(`cols_${section}`, JSON.stringify(reset))
    toast.success('Reset to default')
  }

  const fset = k => e => setFormulas(f => ({ ...f, [k]: parseFloat(e.target.value) || 0 }))

  if (loading) return <div style={{padding:40,color:'var(--text2)',fontFamily:'DM Mono,monospace',fontSize:13}}>Loading settings...</div>

  const TABS = [['markets','Markets'],['formulas','Formulas'],['lists','Lists']]

  return (
    <div style={{padding:24, maxWidth:900, fontFamily:'DM Sans,sans-serif'}}>

      {/* Page header */}
      <div style={{marginBottom:24}}>
        <h2 style={{fontSize:18,fontWeight:700,color:'var(--text)',letterSpacing:-0.3}}>Settings</h2>
        <p style={{fontSize:12,color:'var(--text3)',marginTop:3,fontFamily:'DM Mono,monospace'}}>Manage markets, formulas and column visibility</p>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:2,marginBottom:24,background:'var(--bg)',borderRadius:10,padding:4,width:'fit-content',border:'1px solid var(--border)'}}>
        {TABS.map(([id,label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding:'7px 18px', border:'none', borderRadius:8, cursor:'pointer',
            fontSize:12.5, fontWeight:600, transition:'all 0.15s',
            background: activeTab===id ? 'var(--bg3)' : 'transparent',
            color: activeTab===id ? 'var(--text)' : 'var(--text3)',
            boxShadow: activeTab===id ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
          }}>{label}</button>
        ))}
      </div>

      {/* MARKETS TAB */}
      {activeTab === 'markets' && (
        <div>
          <p style={{fontSize:13,color:'var(--text2)',marginBottom:16,lineHeight:1.6}}>
            Manage which markets are active. New products auto-create records for active markets only. You can add any custom market code.
          </p>

          {/* Active markets list */}
          <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:16}}>
            {activeMarkets.map(m => (
              <div key={m} style={{
                display:'flex',alignItems:'center',gap:8,padding:'7px 12px',
                background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.25)',
                borderRadius:8,fontFamily:'DM Mono,monospace',fontSize:12,fontWeight:700,color:'var(--accent)'
              }}>
                {m}
                <button onClick={()=>toggleMarket(m)} style={{
                  background:'none',border:'none',cursor:'pointer',color:'var(--text3)',
                  fontSize:14,lineHeight:1,padding:0,display:'flex',alignItems:'center'
                }}>×</button>
              </div>
            ))}
          </div>

          {/* Add custom market */}
          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:20,
            background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:10,padding:12}}>
            <div style={{flex:1}}>
              <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',
                letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',marginBottom:5}}>Add Market</div>
              <input
                placeholder="e.g. IT, PL, NL, BE, AE..."
                value={customMarket||''}
                onChange={e=>setCustomMarket(e.target.value.toUpperCase())}
                onKeyDown={e=>e.key==='Enter'&&addCustomMarket()}
                maxLength={10}
                style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:7,
                  color:'var(--text)',padding:'7px 10px',fontSize:13,fontFamily:'DM Mono,monospace',
                  outline:'none',width:200}}
              />
            </div>
            <button className="btn btn-secondary" onClick={addCustomMarket} style={{alignSelf:'flex-end'}}>
              + Add
            </button>
          </div>

          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <button className="btn btn-primary" onClick={saveMarkets} disabled={saving}>
              {saving ? 'Saving...' : 'Save Markets'}
            </button>
            <span style={{fontSize:12,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>
              {activeMarkets.length} active markets
            </span>
          </div>
        </div>
      )}

      {/* FORMULAS TAB */}
      {activeTab === 'lists' && (
        <div>
          <p style={{fontSize:13,color:'var(--text2)',marginBottom:20,lineHeight:1.6}}>
            Manage dropdown lists used across sections. Changes apply immediately.
          </p>

          {/* Reusable list manager component inline */}
          {[
            { key:'who_creates', label:'Funnel Builders', desc:'Used in Funnel Building → Who Creates Funnel dropdown', state:funnelBuilders, setter:setFunnelBuilders, newVal:newFunnelBuilder, newSetter:setNewFunnelBuilder, placeholder:'e.g. Jane Doe' },
            { key:'editors', label:'Creative Editors', desc:'Used in Creatives → Editor dropdown', state:editors, setter:setEditors, newVal:newEditor, newSetter:setNewEditor, placeholder:'e.g. John Smith' },
            { key:'ad_accounts', label:'Ad Accounts', desc:'Used in Ad Scheduling → Ad Account dropdown', state:adAccounts, setter:setAdAccounts, newVal:newAdAccount, newSetter:setNewAdAccount, placeholder:'e.g. SE - Main Account' },
          ].map(list => (
            <div key={list.key} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:14}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:4}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:2}}>{list.label}</div>
                  <div style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{list.desc}</div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => saveList(list.key, list.state)}>💾 Save</button>
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6,margin:'12px 0',minHeight:32}}>
                {list.state.map((item,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',
                    background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:20,fontSize:12,color:'var(--text)'}}>
                    {item}
                    <button onClick={() => list.setter(prev => prev.filter((_,j)=>j!==i))}
                      style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:13,lineHeight:1,padding:0}}>×</button>
                  </div>
                ))}
                {list.state.length === 0 && <span style={{fontSize:12,color:'var(--text3)'}}>No items yet</span>}
              </div>
              <div style={{display:'flex',gap:8}}>
                <input value={list.newVal} onChange={e => list.newSetter(e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter' && list.newVal.trim()) { list.setter(prev=>[...prev,list.newVal.trim()]); list.newSetter('') }}}
                  placeholder={list.placeholder}
                  style={{flex:1,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:7,
                    color:'var(--text)',padding:'7px 10px',fontSize:12,outline:'none'}}/>
                <button className="btn btn-secondary" onClick={() => { if(list.newVal.trim()){list.setter(prev=>[...prev,list.newVal.trim()]);list.newSetter('')}}}>+ Add</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'formulas' && (
        <div>
          <p style={{fontSize:13,color:'var(--text2)',marginBottom:20,lineHeight:1.6}}>
            Control all financial formulas used across the app.
          </p>

          {[
            {
              title:'Currency Conversion', icon:'💱', accent:'var(--accent)',
              fields:[{ key:'usd_eur_rate', label:'USD → EUR Rate', desc:'Used to convert sourcing and shipping costs', suffix:'', example:'e.g. 0.93' }]
            },
            {
              title:'Shopify Fees', icon:'🛒', accent:'var(--accent3)',
              fields:[
                { key:'shopify_pct', label:'Shopify Percentage', desc:'Shopify Payments processing fee', suffix:'%', example:'e.g. 4.99' },
                { key:'shopify_fixed', label:'Shopify Fixed Fee', desc:'Fixed fee per transaction', suffix:'£', example:'e.g. 0.30' },
              ]
            },
            {
              title:'Currency Conversion Fee', icon:'💰', accent:'var(--gold)',
              fields:[{ key:'currency_conv_pct', label:'Conversion Fee', desc:'Fee for currency conversion on international sales', suffix:'%', example:'e.g. 2.0' }]
            },
          ].map(card => (
            <div key={card.title} style={{
              background:'var(--bg2)', border:'1px solid var(--border)',
              borderRadius:12, padding:18, marginBottom:12,
              borderLeft:`3px solid ${card.accent}`,
            }}>
              <div style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:14,display:'flex',alignItems:'center',gap:8}}>
                <span>{card.icon}</span>{card.title}
              </div>
              {card.fields.map(f => (
                <div key={f.key} style={{marginBottom:12,display:'flex',alignItems:'flex-start',gap:16,flexWrap:'wrap'}}>
                  <div style={{flex:'0 0 200px'}}>
                    <label style={{fontSize:10,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',marginBottom:4,display:'block'}}>{f.label}</label>
                    <div style={{fontSize:11,color:'var(--text3)',marginTop:2,lineHeight:1.5}}>{f.desc}</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <input type="number" step="0.01" value={formulas[f.key]} onChange={fset(f.key)}
                      style={{width:120,fontFamily:'DM Mono,monospace',fontSize:14,fontWeight:600}} />
                    {f.suffix && <span style={{fontSize:12,color:'var(--text2)',fontWeight:600,fontFamily:'DM Mono,monospace'}}>{f.suffix}</span>}
                    <span style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{f.example}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Formula breakdown */}
          <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:18,marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.8px',fontFamily:'DM Mono,monospace'}}>How it all fits together</div>
            {[
              ['Sourcing (€)','Sourcing USD × USD/EUR Rate'],
              ['Shipping (€)','Shipping USD × USD/EUR Rate'],
              ['Total COGs','Sourcing € + Shipping €'],
              ['Shopify Fee','Price × '+formulas.shopify_pct+'% + £'+formulas.shopify_fixed],
              ['Currency Conv','Price × '+formulas.currency_conv_pct+'%'],
              ['Total Fees','Shopify Fee + Currency Conv'],
              ['Profit (€)','Price − Total COGs − Total Fees'],
              ['BE ROAS','Price ÷ Profit'],
            ].map(([label,formula],i) => (
              <div key={label} style={{
                display:'flex', gap:12, padding:'7px 0',
                borderBottom: i < 7 ? '1px solid var(--border)' : 'none',
                fontSize:12,
              }}>
                <div style={{width:140,fontWeight:600,color:'var(--text2)',flexShrink:0,fontFamily:'DM Mono,monospace'}}>{label}</div>
                <div style={{color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{formula}</div>
              </div>
            ))}
          </div>

          <button className="btn btn-primary" onClick={saveFormulas} disabled={saving}>
            {saving ? 'Saving...' : 'Save Formulas'}
          </button>
          <p style={{fontSize:11,color:'var(--text3)',marginTop:8,fontFamily:'DM Mono,monospace'}}>
            Changes apply to newly calculated values. Edit and save a product to recalculate existing records.
          </p>
          <div style={{marginTop:28,paddingTop:20,borderTop:'1px solid var(--border)'}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Currency Rates (EUR → X)</div>
            <p style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>Used in Ad Scheduling campaign names to convert EUR costs into each market's currency. 1 EUR = X units. (SE→SEK, DK→DKK, NO→NOK; FI & NL-BE use EUR.)</p>
            <div style={{display:'flex',flexDirection:'column',gap:10,maxWidth:320}}>
              {['SEK','DKK','NOK'].map(cur => (
                <div key={cur} style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:80,fontWeight:600,fontSize:13,fontFamily:'DM Mono,monospace'}}>EUR → {cur}</div>
                  <input type="number" step="0.01" value={currencyRates[cur] ?? ''} onChange={e => setCurrencyRates(r => ({ ...r, [cur]: parseFloat(e.target.value) || 0 }))}
                    style={{flex:1,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:'var(--text)',padding:'6px 10px',fontSize:13,outline:'none'}} />
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{marginTop:14}} onClick={saveCurrencyRates} disabled={saving}>
              {saving ? 'Saving...' : 'Save Currency Rates'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
